import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Redirect, useLocation } from "react-router-dom";
import { RouteName } from "../../constants/Constants";
import hubspotCobrowseSignalR, { inspectToken } from "../../services/hubspotCobrowseSignalR";
import { setPageTitle } from "../../shared/utility";
import "./HubSpotCobrowse.scss";

// Short TTL so a deliberate reopen later still opens a tab.
const OPEN_LOCK_KEY = "_blitzz_cobrowse_open_lock";
const OPEN_LOCK_TTL_MS = 5000;

// How long we wait for the ticket's visitor device to come online before we give
// up, tear down the socket, and show the offline state. The visitor's page sends
// a heartbeat over the socket; if they've closed it (or never had it open) no
// heartbeat ever arrives, so an open-ended loader would spin forever. The agent
// can re-arm the search with "Try again".
const FIND_DEVICE_TIMEOUT_MS = 30000;

// Specific, non-retryable "can't proceed" copy for the failures we can name. Everything
// the iframe needs arrives on the URL (token, thread, domainUrl); when one is
// missing/invalid there's nothing to retry. The `error` view (Close button) shows one of
// these when we know the cause, and a generic "Something went wrong" default otherwise.
const FATAL = {
    // Token present but its JWT `exp` is in the past — the sign-in has expired.
    // Reopening from the ticket re-authenticates and issues a fresh token.
    EXPIRED: {
        title: "Your sign-in has expired",
        text: "Your sign-in is no longer valid. Close this window and start co-browse again from the ticket to refresh it.",
    },
    // No `thread` (conversation id) — e.g. a ticket not created from a visitor chat has no
    // originating conversation, so there is no live session to match.
    NO_THREAD: {
        title: "No conversation linked",
        text: "This ticket isn’t linked to a co-browse conversation, so there’s no live session to join. Start co-browse from a ticket opened by the visitor’s chat.",
    },
    // Matched the visitor but couldn't build a session link (missing domainUrl or
    // sessionID). Surface it instead of a dead-end "online" screen with no Join button.
    NO_LINK: {
        title: "Couldn’t open the session",
        text: "We reached the visitor but couldn’t build the session link. Close this window and start co-browse again from the ticket.",
    },
};

const deviceName = (d) => {
    const br = (d.deviceInfo && d.deviceInfo.browser) || {};
    const os = (d.deviceInfo && d.deviceInfo.os) || {};
    if (d.reference) return d.reference;
    if (br.name && os.name) return `${br.name} on ${os.name}`;
    return `Visitor ${d.sessionID || ""}`;
};

// Guard against a duplicate iframe instance opening the session tab twice.
// localStorage is shared across same-origin iframes, so a 2nd instance sees the
// lock and skips. Returns false if another instance just opened it.
const shouldOpenOnce = (key) => {
    try {
        const now = Date.now();
        const raw = localStorage.getItem(OPEN_LOCK_KEY);
        if (raw) {
            const lock = JSON.parse(raw);
            if (lock && lock.k === key && now - lock.t < OPEN_LOCK_TTL_MS) return false;
        }
        localStorage.setItem(OPEN_LOCK_KEY, JSON.stringify({ k: key, t: now }));
        return true;
    } catch (error) {
        return true;
    }
};

// Ask HubSpot's openIframeModal host to close this modal. The UI-extensions SDK
// (platformVersion 2025.2) documents the close signal as
// postMessage(JSON.stringify({action:"DONE"}), "*") to window.top. We post to both top
// and parent so a nested modal iframe is covered either way.
// IMPORTANT: HubSpot ignores the message unless the posting page's origin matches the
// `uri` origin passed to openIframeModal (i.e. this page must be served from
// BLITZZ_WEB_URL). A host/redirect mismatch makes the modal silently fail to close.
const closeModalSoon = (ms) => {
    setTimeout(() => {
        const msg = JSON.stringify({ action: "DONE" });
        try {
            window.parent.postMessage(msg, "*");
        } catch (error) {}
        try {
            window.top.postMessage(msg, "*");
        } catch (error) {}
    }, ms);
};

/*
    Rendered (without layout/sidebar) inside the HubSpot UI-extension "iframe
    modal" via openIframeModal. Params arrive on the URL: token, refreshToken,
    domainUrl, thread (the ticket's HubSpot conversation/thread id). It opens its
    own SignalR socket via services/hubspotCobrowseSignalR — separate from the
    app-wide signalR.js, which is tied to the logged-in agent — and watches
    visitor device heartbeats. When the device whose heartbeat carries this
    ticket's thread comes online, it auto-opens that visitor's co-browse session
    in a new tab and closes the modal. A "Join session" button is always shown as
    a fallback (browsers may block the auto-opened tab — see note below).

    View state machine:
      "connecting"   → connecting the socket / searching for the ticket's device (loader)
      "connected"    → device is online; opening the session + Join fallback
      "offline"      → FIND_DEVICE_TIMEOUT_MS elapsed (post-connect) with no match;
                       socket torn down, "Try again" re-arms the search
      "connectError" → socket failed to connect; "Try again" re-arms
      "error"        → no auth token on the URL; "Close" the modal and reopen from the ticket
*/
const HubSpotCobrowse = () => {
    const location = useLocation();

    // This view only ever runs inside HubSpot's iframe modal. CSP frame-ancestors
    // restricts WHO may frame it; this guards the other axis — a direct, top-level hit
    // of the URL in the agent portal (window.self === window.top) is sent to login.
    const isEmbedded = window.self !== window.top;

    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const token = params.get("token") || "";
    const refreshToken = params.get("refreshToken") || "";
    const domainUrl = params.get("domainUrl") || "";
    // ticket's HubSpot conversation/thread id — match the device whose heartbeat carries this
    const thread = params.get("thread") || "";

    // Everything needed comes from the URL, so every non-retryable failure is knowable at
    // mount — before opening a socket. This is how we avoid sitting on the loader for 30s
    // only to show a misleading "Visitor is offline". `initialFatal` is the specific
    // message when we can name the cause; null falls back to the generic default below.
    const tokenInfo = useMemo(() => inspectToken(token), [token]);
    let initialFatal = null;
    if (token) {
        if (tokenInfo.expired) initialFatal = FATAL.EXPIRED;
        else if (!thread) initialFatal = FATAL.NO_THREAD;
    }
    // Can't start the session: no token at all, or a specific blocker above.
    const cannotStart = !token || initialFatal !== null;

    // view: "connecting" | "connected" | "offline" | "connectError" | "error".
    const [view, setView] = useState(cannotStart ? "error" : "connecting");
    // Message shown by the "error" view (null → the generic default in the render).
    const [fatal, setFatal] = useState(initialFatal);
    const [connected, setConnected] = useState(null); // { name, url }
    // Bumping `attempt` re-runs the connect effect — this is how "Try again" rearms
    // the socket + the find timeout from a clean slate.
    const [attempt, setAttempt] = useState(0);
    const connectAttemptedRef = useRef(false);

    useEffect(() => {
        setPageTitle("HubSpot Co-browse");
    }, []);

    const handleRetry = useCallback(() => {
        connectAttemptedRef.current = false;
        setConnected(null);
        setView("connecting");
        setAttempt((n) => n + 1);
    }, []);

    // Clicking "Join session" opens the session tab (the anchor's default action);
    // close the modal right after, same as the auto-open path. Deferred (next tick)
    // so the new tab opens before the modal tears down.
    const handleJoinClick = useCallback(() => {
        closeModalSoon(0);
    }, []);

    // Shared "Try again" button — re-arms the connect cycle (used by the offline and
    // connect-error states). Safe to reuse the same element: only one state renders.
    const retryButton = (
        <button type="button" className="retry-btn" onClick={handleRetry}>
            <svg className="retry-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.65 6.35A7.958 7.958 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
            </svg>
            Try again
        </button>
    );

    useEffect(() => {
        // Any mount-time blocker (no/expired/account-less token, no conversation id) is
        // already shown via the error view; don't open a socket for it.
        if (cannotStart || !isEmbedded) return undefined;

        // Per-attempt locals (reset on every effect run / retry).
        let offlineTimer = null;
        let matched = false;
        let cancelled = false;

        // Mirrors redirectToCobrowse() in the Zendesk app.
        const buildConnectUrl = (device) => {
            if (!domainUrl || !device.sessionID) return "";
            return (
                `${domainUrl}/cobrowse/call/${device.sessionID}` +
                `?t1=${encodeURIComponent(token)}` +
                `&r1=${encodeURIComponent(refreshToken)}`
            );
        };

        // Only act on the device tied to this ticket (its heartbeat carries `thread`).
        // If no thread was passed, there is nothing to match.
        const findTicketDevice = (devices) => {
            if (!thread) return null;
            return devices.find((d) => String(d.hubspotConversationId || "") === String(thread)) || null;
        };

        // Give up waiting: stop the loader, drop the socket, surface the offline state.
        const goOffline = () => {
            if (matched) return;
            // Lock the attempt so a heartbeat arriving during teardown can't flip us
            // back to "connected" after we've already given up.
            matched = true;
            setView("offline");
            hubspotCobrowseSignalR.Disconnect();
        };

        const handleDevicesChanged = (devices) => {
            // Ignore further churn once we've matched, given up (offline), or torn
            // down this attempt (unmount/retry) — avoids a stale-closure setState/open.
            if (matched || cancelled) return;

            const device = findTicketDevice(devices);

            // No matching device yet → keep the loader until the timeout fires.
            // (The SDK can attach hubspotConversationId a beat after the chat starts,
            // so the match may take a few heartbeats.)
            if (!device) return;

            // Found this ticket's device → lock the attempt and stop the timeout.
            matched = true;
            if (offlineTimer) {
                clearTimeout(offlineTimer);
                offlineTimer = null;
            }

            const url = buildConnectUrl(device);

            // We matched the visitor but can't build a session link (missing
            // domainUrl or sessionID). Don't claim "online" with no way to join —
            // surface a clear, non-retryable error instead of a dead-end screen.
            if (!url) {
                setFatal(FATAL.NO_LINK);
                setView("error");
                return;
            }

            // Open its session in a new tab (once). `url` is guaranteed non-empty here
            // (the !url guard above bailed otherwise).
            if (!connectAttemptedRef.current) {
                connectAttemptedRef.current = true;
                const sessKey = String(device.sessionID || device.device_id || url);
                if (shouldOpenOnce(sessKey)) {
                    let win = null;
                    try {
                        win = window.open(url, "_blank");
                    } catch (error) {}
                    // A popup blocker may return null OR a window that's already
                    // closed — treat both as "blocked". Only auto-close the modal
                    // when a tab genuinely opened; otherwise leave it open so the
                    // agent can click "Join session".
                    const opened = !!win && !win.closed;
                    // Tab opened → close the modal immediately (next tick, so
                    // window.open has returned); no UX delay needed.
                    if (opened) closeModalSoon(0);
                } else {
                    // A duplicate iframe instance already opened the tab → close this one.
                    closeModalSoon(300);
                }
            }

            setConnected({ name: deviceName(device), url });
            setView("connected");
        };

        // Open the socket first; only once it's actually CONNECTED do we start the
        // find-device window. This way a slow/struggling connection keeps showing
        // "connecting" (honest) instead of wrongly reporting the visitor offline, and a
        // connection that never establishes rejects below into the error state. The
        // find timeout therefore measures "time since connected", not "time since mount".
        hubspotCobrowseSignalR
            .Connect({
                token,
                socketUrl: process.env.REACT_APP_COBROWSE_SOCKET_CONNECTION_URL,
                onDevicesChanged: handleDevicesChanged,
            })
            .then(() => {
                // Connected. If a device didn't already match during connect, start the
                // window to find the ticket's device.
                if (cancelled || matched || offlineTimer) return;
                offlineTimer = setTimeout(goOffline, FIND_DEVICE_TIMEOUT_MS);
            })
            .catch(() => {
                // The socket failed to connect (network down, WebSocket blocked, server
                // rejected the token) — a connection problem, NOT a missing token. Show
                // the retryable connect-error state. (Token health — missing/expired/no
                // domain — is already screened at mount via `initialFatal`.)
                if (!cancelled) setView("connectError");
            });

        // Graceful cleanup: clear the timer and stop the socket when the iframe is
        // torn down or a retry re-runs this effect.
        return () => {
            cancelled = true;
            if (offlineTimer) {
                clearTimeout(offlineTimer);
                offlineTimer = null;
            }
            hubspotCobrowseSignalR.Disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, attempt]);

    // Direct, non-framed access to this route → not a real HubSpot session; bounce to login.
    if (!isEmbedded) return <Redirect to={RouteName.LOGIN} />;

    return (
        <div className="hubspot-cobrowse-page">
            <div className="hubspot-cobrowse-list">
                {view === "error" && (
                    <div className="state">
                        <div className="offline-badge">
                            <span className="icon-warning" />
                        </div>
                        <div className="state-title">{fatal ? fatal.title : "Something went wrong"}</div>
                        <div className="state-text">
                            {fatal
                                ? fatal.text
                                : "We couldn’t start the co-browse session. Close this window and start it again from the ticket."}
                        </div>
                        <button type="button" className="retry-btn" onClick={() => closeModalSoon(0)}>
                            Close
                        </button>
                    </div>
                )}

                {view === "connecting" && (
                    <div className="state">
                        <div className="spinner" />
                        <div className="state-title">Reaching the visitor</div>
                        <div className="state-text">
                            This connects automatically while the visitor's page is open.
                        </div>
                    </div>
                )}

                {view === "offline" && (
                    <div className="state">
                        <div className="offline-badge">
                            <span className="icon-network-troubleshoot" />
                        </div>
                        <div className="state-title">Visitor is offline</div>
                        <div className="state-text">
                            The visitor may have closed the page or lost connection. Ask the
                            visitor to reopen it, then try again.
                        </div>
                        {retryButton}
                    </div>
                )}

                {view === "connectError" && (
                    <div className="state">
                        <div className="offline-badge">
                            <span className="icon-network-troubleshoot" />
                        </div>
                        <div className="state-title">Couldn’t connect</div>
                        <div className="state-text">
                            We couldn’t reach the co-browse service. Check your connection, then
                            try again.
                        </div>
                        {retryButton}
                    </div>
                )}

                {view === "connected" && connected && (
                    <div className="state">
                        <div className="check-badge">
                            <span className="icon-check-circle" />
                        </div>
                        <div className="state-title">{connected.name} is online</div>
                        <div className="state-text">
                            If the session didn’t open in a new tab, click “Join session”.
                        </div>
                        {connected.url && (
                            <a
                                className="join-btn"
                                href={connected.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={handleJoinClick}
                            >
                                Join session
                            </a>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default HubSpotCobrowse;
